import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';

import { CLINICAL_TRIALS_API_URL } from '../tokens';
import { IClinicalTrialSubject, IResearchStudy, IResearchSubject } from '../interfaces';
import { getQualityScore, getResearchStudyDisplay, sortByDateFunction } from '../utils';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ClinicalTrialsUIService {

  candidateEligibility = new Subject<boolean>();
  candidateEligibility$: Observable<boolean>;
  criteriaChanged = new Subject<fhir4.QuestionnaireResponseItem[]>();
  criteriaChanged$: Observable<fhir4.QuestionnaireResponseItem[]>;

  constructor(
    @Inject(CLINICAL_TRIALS_API_URL)
    private readonly apiUrl: string,

    private readonly httpClient: HttpClient,
  ) {
    this.criteriaChanged$ = this.criteriaChanged.asObservable();
    this.candidateEligibility$ = this.candidateEligibility.asObservable();
  }

  onCriteriaChanged(questionnaireResponseItem: fhir4.QuestionnaireResponseItem[]): void {
    this.criteriaChanged.next(questionnaireResponseItem);
  }

  onEligibilityUpdated(ineligible: boolean): void {
    this.candidateEligibility.next(ineligible);
  }

  getClinicalTrialSubjects(associatedPartyID: string, patientID: string): Observable<IClinicalTrialSubject[]> {
    return this.httpClient.get<fhir4.Bundle>(`${ this.apiUrl }/ResearchSubject?patient.identifier=${ patientID }&study.associatedParty.identifier=${ associatedPartyID }&_include=*`)
                    .pipe(
                      map((bundle: fhir4.Bundle) => {
                        if (bundle.entry) {
                          const clinicalTrialSubjects: IClinicalTrialSubject[] = bundle.entry.filter(entry => entry.resource.resourceType === 'ResearchSubject').map(entry => {
                            const researchSubject: IResearchSubject = entry.resource as unknown as IResearchSubject;
                            const researchStudy = bundle.entry.find( item => researchSubject.study.reference.includes(item.resource.id) && item.resource.resourceType === 'ResearchStudy').resource as IResearchStudy;
                            const researchStudyDisplay = getResearchStudyDisplay(researchStudy);

                            const clinicalTrialSubject: IClinicalTrialSubject = {
                              researchSubject,
                              researchStudyDisplay
                            };

                            return clinicalTrialSubject;
                          });

                          return clinicalTrialSubjects;
                        }

                        return [];
                      })
                    );
  }

  getCriteria(clinicalTrialSubject: IClinicalTrialSubject): Observable<{group: fhir4.Group, questionnaireResponse: fhir4.QuestionnaireResponse }> {
    const subscribeables = [
      this._getGroup(clinicalTrialSubject.researchStudyDisplay.researchStudy),
      this._getQuestionnaireResponse(clinicalTrialSubject.researchSubject.id)
    ];

    return forkJoin(subscribeables)
            .pipe(
              map(res => {
                const group: fhir4.Group = res[0] as fhir4.Group;
                const questionnaireResponse: fhir4.QuestionnaireResponse = res[1] as fhir4.QuestionnaireResponse;
                return { group, questionnaireResponse };
              })
            );
  }

  _getGroup(study: IResearchStudy): Observable<fhir4.Group> {
    return this.httpClient.get<fhir4.Group>(`${ this.apiUrl }/${ study.recruitment?.eligibility?.reference }`);
  }

  _getQuestionnaireResponse(id: string): Observable<fhir4.QuestionnaireResponse> {
    const headers: HttpHeaders = new HttpHeaders({
      'Cache-Control': 'no-cache',
    });

    return this.httpClient
                .get<fhir4.Bundle>(`${ this.apiUrl }/QuestionnaireResponse?subject=ResearchSubject/${ id }`, { headers })
                .pipe(
                  map(bundle => {
                    const questionnaireResponses = bundle?.entry?.map(entry => entry.resource) as fhir4.QuestionnaireResponse[] || [];
                    return questionnaireResponses.sort((a, b) => {
                      return sortByDateFunction(a.meta?.lastUpdated, b.meta?.lastUpdated);
                    })[0];
                  })
                );
  }

  updateClinicalTrialSubject(questionnaireResponse: fhir4.QuestionnaireResponse, researchSubject: IResearchSubject): Observable<{questionnaireResponse: fhir4.QuestionnaireResponse, researchSubject: IResearchSubject}> {
    const subscribeables = [
      this._updateCandidateCriteria(questionnaireResponse),
      this._updateResearchSubject(researchSubject),
    ];

    return forkJoin(subscribeables)
           .pipe(
             map(res => ({
                questionnaireResponse: res[0] as fhir4.QuestionnaireResponse,
                researchSubject: res[1] as IResearchSubject,
             }))
           );
  }

  _updateCandidateCriteria(questionnaireResponse: fhir4.QuestionnaireResponse): Observable<fhir4.QuestionnaireResponse> {
    return this.httpClient.post<fhir4.QuestionnaireResponse>(`${ this.apiUrl }/QuestionnaireResponse`, questionnaireResponse);
  }

  _updateResearchSubject(researchSubject: IResearchSubject): Observable<IResearchSubject> {
    const qualityScore = getQualityScore(researchSubject.extension);

    return this.httpClient.put<IResearchSubject>(`${ this.apiUrl }/ResearchSubject/${ researchSubject.id }?numerator=${ qualityScore.numerator.value }&denominator=${ qualityScore.denominator.value }`, {});
  }
}
